import {
  Component,
  ChangeDetectionStrategy,
  inject,
  HostBinding,
  input,
  output,
  signal,
  viewChild,
  computed,
  effect,
} from '@angular/core';
import clsx from 'clsx';

import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import {
  AdminPanelHeaderNavigation,
  AVAILABLE_LANGUAGES,
  getImageCdnUrl,
  SUPPORTED_LANGUAGES,
} from '@pedix-workspace/utils';
import { ApiV1_Establishment, ApiV1_Product } from '@pedix-workspace/api-v1';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { RouterLink } from '@angular/router';
import {
  IconCheckoutComponent,
  IconCloseComponent,
  IconMenuComponent,
  IconSearchComponent,
} from '@pedix-workspace/angular-ui-icons';

import { TranslocoDirective } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { InputTextComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { FormsModule } from '@angular/forms';
import { EnvironmentService } from '@pedix-workspace/angular-utils';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    IconMenuComponent,
    IconCheckoutComponent,
    IconSearchComponent,
    IconCloseComponent,
    RouterLink,
    ButtonComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbDropdownToggle,
    InputTextComponent,
    FormsModule,
  ],
})
export class HeaderComponent {
  private analyticsService = inject(AnalyticsService);
  private environmentService = inject(EnvironmentService);

  readonly searchInput = viewChild.required(InputTextComponent);

  readonly establishment = input.required<ApiV1_Establishment>();
  readonly hasCartItems = input.required<boolean>();
  readonly displayAdminButton = input.required<boolean>();
  readonly selectedLanguage = input.required<SUPPORTED_LANGUAGES>();
  readonly availableLanguages = input.required<AVAILABLE_LANGUAGES>();
  readonly searchText = input.required<string>();
  readonly subsidiarySelector = input.required<boolean>();
  readonly isSubsidiaryLanding = input.required<boolean>();

  readonly searchEnabled = signal(false);
  readonly filteredProducts = signal<ApiV1_Product[]>([]);

  readonly hasSubsidiaries = computed(() => this.establishment()?.subsidiaries?.length > 0);
  readonly establishmentName = computed(() => this.establishment().name);
  readonly establishmentLogo = computed(() => {
    const logo = this.establishment().logo;

    if (!logo) {
      return null;
    }
    return getImageCdnUrl(logo, { size: '250x250' }, this.environmentService.env);
  });
  readonly adminUrl = computed(() => environment.adminUrl);

  readonly clickLateralMenu = output<void>();
  readonly searchTextChange = output<string>();
  readonly languageSelected = output<SUPPORTED_LANGUAGES>();

  @HostBinding('class')
  get classes() {
    return clsx({
      'search-enabled': this.searchEnabled(),
    });
  }

  constructor() {
    let lastSearchText;

    effect(() => {
      if (this.searchText() && this.searchText() !== lastSearchText) {
        this.onSearchOpen();
      }
      lastSearchText = this.searchText();
    });
  }

  onClickLateralMenu($event) {
    this.clickLateralMenu.emit($event);
  }

  clickOption(header_menu: AdminPanelHeaderNavigation) {
    this.analyticsService.logEvent('adminpanel_headeroptions_clicked', { header_menu });
  }

  onSearchOpen() {
    this.searchEnabled.set(true);

    setTimeout(() => {
      this.searchInput()?.focus();
    });
  }

  onSearchClose() {
    this.searchEnabled.set(false);

    this.searchTextChange.emit('');
  }

  onSearchKeyUp($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.searchTextChange.emit(this.searchInput()?.value);
    } else if ($event.key === 'Escape') {
      this.searchInput()?.writeValue('');
      this.searchInput()?.input?.nativeElement?.blur();
    }
  }

  onSearchFocusOut() {
    this.searchTextChange.emit(this.searchInput()?.value);

    if (!this.searchInput()?.value) {
      this.onSearchClose();
    }
  }
}
